import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import img from '../../../assests/images/Images';
import '../ServiceCards.css';

/* ─── Static Data ─────────────────────────────────────────────── */
const SERVICES = [
  {
    num: '01',
    heading: 'Custom Web & Mobile Development',
    content: 'We build fast, secure web portals, SaaS platforms, and mobile apps tailored to your business goals and built for long-term reliability.',
    focusAreas: [
      'Business Web Portals',
      'SaaS & Web Apps',
      'Mobile Applications',
      'Custom Software',
      'API Integrations',
    ],
    ctaText: 'Explore Development Services',
    ctaLink: '/services/web-development',
    accentColor: '#1a2f6e',
    badgeClass: 'sipl-bg-navy',
    image: img.customweb,
  },
  {
    num: '02',
    heading: 'Enterprise & Business Systems',
    content: 'Tailored software that automates repetitive tasks, connects separate departments, and gives management real-time visibility across operations.',
    focusAreas: [
      'Custom ERP Systems',
      'HRM & Payroll Tools',
      'CRM & Sales Pipelines',
      'LMS Platforms',
      'Internal Workflow Automation',
    ],
    ctaText: 'Explore Business Solutions',
    ctaLink: '/services/business-software',
    accentColor: '#4caf50',
    badgeClass: 'sipl-bg-green',
    image: img.business,
  },
  {
    num: '03',
    heading: 'UI/UX & Product Design',
    content: 'We turn complex workflows into intuitive, user-friendly digital experiences through research, wireframing, clickable prototypes, and design systems.',
    focusAreas: [
      'User Research & Flow Design',
      'Interface (UI) Design',
      'Interactive Prototypes',
      'Design Systems',
      'Responsive Web Design',
    ],
    ctaText: 'Explore Product Design',
    ctaLink: '/services/ui-ux-design',
    accentColor: '#0d9488',
    badgeClass: 'sipl-bg-teal',
    image: img.uiux,
  },
  {
    num: '04',
    heading: 'Cloud Infrastructure & DevOps',
    content: 'Reliable cloud setup, hosting configuration, and automated CI/CD pipelines to keep your software fast, secure, and online 24/7.',
    focusAreas: [
      'Cloud Deployment (AWS/Azure)',
      'Application Hosting',
      'CI/CD Pipelines',
      'Server Monitoring & Security',
      'Performance Optimization',
    ],
    ctaText: 'Explore Cloud Services',
    ctaLink: '/services/cloud-devops',
    accentColor: '#f97316',
    badgeClass: 'sipl-bg-orange',
    image: img.devops,
  },
  {
    num: '05',
    heading: 'Digital Marketing & SEO',
    content: 'Data-driven SEO, Google Ads, and performance marketing strategies to increase search visibility, drive qualified traffic, and scale customer conversions.',
    focusAreas: [
      'Search Engine Optimization (SEO)',
      'Google Ads & Performance Max',
      'Keyword Strategy & Ranking',
      'Content Marketing Strategy',
      'GA4 Analytics & Attribution',
    ],
    ctaText: 'Explore Marketing & SEO',
    ctaLink: '/services/digital-marketing',
    accentColor: '#2563eb',
    badgeClass: 'sipl-bg-navy',
    image: img.digital,
  },
];

/* ─── Scroll-Linked Hook ──────────────────────────────────────────
   Drives --sc-progress (0→1) on each card ref as it scrolls into
   view. Reverses naturally when scrolling up.
   Stagger: card i starts animating when its top crosses vh + i×150px.
   Respects prefers-reduced-motion.
──────────────────────────────────────────────────────────────── */
function useServiceCardScroll(count) {
  const refs = useRef([]);

  if (refs.current.length !== count) {
    refs.current = Array.from({ length: count }, (_, i) => refs.current[i] || null);
  }

  useEffect(() => {
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) {
      refs.current.forEach((el) => {
        if (el) {
          el.style.setProperty('--sc-progress', '1');
          el.classList.add('sc-revealed');
        }
      });
      return;
    }

    function updateAll() {
      const vh = window.innerHeight;
      refs.current.forEach((el, i) => {
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const pos = rect.top;

        // Smooth cascade capped so cards display without excessive delay
        const start = vh + Math.min(i * 80, 240);  // progress = 0 here
        const end = vh * 0.45;     // progress = 1 here

        let p;
        if (pos >= start) p = 0;
        else if (pos <= end) p = 1;
        else p = 1 - (pos - end) / (start - end);

        el.style.setProperty('--sc-progress', p.toFixed(4));
        if (p >= 0.99) {
          el.classList.add('sc-revealed');
        } else {
          el.classList.remove('sc-revealed');
        }
      });
    }

    let ticking = false;
    function onScrollOrResize() {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(() => {
          updateAll();
          ticking = false;
        });
      }
    }

    updateAll();
    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize, { passive: true });

    return () => {
      window.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [count]);

  return refs;
}

/* ─── ServiceCardsSection ─────────────────────────────────────────
   Drop-in component for the Home page Section 4 cards grid.
   Owns its own data, icons, scroll hook, and CSS.
──────────────────────────────────────────────────────────────── */
const ServiceCardsSection = () => {
  const cardRefs = useServiceCardScroll(SERVICES.length);

  return (
    <div className="row g-4">
      {SERVICES.map((s, i) => (
        <div
          key={s.num}
          ref={(el) => { cardRefs.current[i] = el; }}
          className="col-12 col-md-6 col-lg-4 sc-card-wrapper"
        >
          <div
            className="sipl-service-card h-100 p-4"
            style={{
              '--service-accent': s.accentColor,
              background: '#ffffff',
              borderRadius: '16px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 4px 18px rgba(15, 23, 42, 0.04)',
              minHeight: '460px',
            }}
          >
            <div className="d-flex flex-column justify-content-between h-100">
              <div>
                <div className="d-flex align-items-center justify-content-between mb-4">
                  <div className="sipl-service-icon-box">
                    <img
                      src={s.image}
                      alt={s.heading}
                      className="sipl-service-img"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <span
                    className={`badge text-white px-2.5 py-1 rounded-pill small fw-bold`}
                    style={{ backgroundColor: s.accentColor }}
                  >
                    {s.num}
                  </span>
                </div>

                <h3 className="h5 fw-bold text-dark mb-3">{s.heading}</h3>
                <p className="text-muted small mb-4" style={{ lineHeight: '1.65' }}>{s.content}</p>

                <div className="d-flex flex-wrap mb-4" style={{ gap: '6px' }}>
                  {s.focusAreas.map((item) => (
                    <span
                      key={item}
                      className="badge bg-light text-dark border px-2.5 py-1.5 rounded-2 fw-medium"
                      style={{ fontSize: '0.75rem', color: '#334155', borderColor: '#e2e8f0' }}
                    >
                      <span style={{ color: s.accentColor, marginRight: 4 }}>•</span> {item}
                    </span>
                  ))}
                </div>
              </div>

              <Link
                to={s.ctaLink}
                className="text-decoration-none fw-semibold sipl-text-navy sipl-arrow-link d-inline-flex align-items-center gap-1 small mt-auto"
              >
                {s.ctaText} <span>→</span>
              </Link>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ServiceCardsSection;
